import React, { useState, useEffect } from "react";
import {
  TestTube,
  CornerUpLeftIcon,
  SquarePlayIcon,
  Trash2Icon,
  ShieldCheckIcon,
  WorkflowIcon,
  PackagePlusIcon,
  ScrollTextIcon,
  LoaderPinwheelIcon,
  ChevronRight,
  Info,
} from "lucide-react";
import { useNotification } from "../../../hooks";
import {
  useAddEndpoint,
  useDeleteTestCaseFromSuite,
  useGetEndpointsForTestSuite,
  useDeleteChainFromSuite,
  useRunApiSuite,
} from "../../../useQuery";
import ChainAccordion from "./ChainAccordian";
import AddTestCaseModal from "./AddTestCaseModal";
import AddChainModal from "./AddChainModal";

const METHOD_STYLES = {
  GET: { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  POST: { text: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
  PUT: { text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  DELETE: { text: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
  PATCH: { text: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200" },
};

const MethodBadge = ({ method }) => {
  const style = METHOD_STYLES[method?.toUpperCase()] || {
    text: "text-gray-600",
    bg: "bg-gray-50",
    border: "border-gray-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold tracking-wide border ${style.text} ${style.bg} ${style.border}`}
    >
      {method?.toUpperCase() || "N/A"}
    </span>
  );
};

const ActionButton = ({ onClick, icon: Icon, label, variant = "blue", disabled, loading }) => {
  const variants = {
    blue: "bg-blue-600 hover:bg-blue-700 text-white",
    purple: "bg-purple-600 hover:bg-purple-700 text-white",
    green: "bg-green-600 hover:bg-green-700 text-white",
    indigo: "bg-indigo-600 hover:bg-indigo-700 text-white",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait ${variants[variant]}`}
    >
      <Icon size={15} />
      {loading || label}
    </button>
  );
};

const SuiteDetails = ({ suite, onBack, onViewResults, onPerformanceTest }) => {
  const notify = useNotification();
  const [showAddTestCaseModal, setShowAddTestCaseModal] = useState(false);
  const [showAddChainModal, setShowAddChainModal] = useState(false);

  const { mutate: runApiSuite, isPending: runningSuite } = useRunApiSuite({
    onSuccess: () =>
      notify("Suite run started, click on View Suite Results for results", "success"),
    onError: (error) => notify(`Error starting suite: ${error}`, "error"),
  });

  const {
    data: endpoints = [],
    isLoading: loadingSuiteEndpoints,
    refetch: fetchSuiteEndpoints,
  } = useGetEndpointsForTestSuite(suite?.id);

  const { mutate: addToSuite, isPending: addingToSuite } = useAddEndpoint({
    onSuccess: () => {
      notify("Added successfully!", "success");
      fetchSuiteEndpoints(suite.id);
    },
    onError: () => notify("Error adding to suite", "error"),
  });

  const { mutate: deleteTestCase } = useDeleteTestCaseFromSuite({
    onSuccess: () => {
      notify("Test Case Deleted!", "success");
      fetchSuiteEndpoints(suite.id);
    },
    onError: (error) => notify(`Error Deleting Test Case: ${error}`, "error"),
  });

  const { mutate: deleteChain } = useDeleteChainFromSuite({
    onSuccess: () => {
      notify("Chain Deleted!", "success");
      fetchSuiteEndpoints(suite.id);
    },
    onError: (error) => notify(`Error Deleting Chain: ${error}`, "error"),
  });

  useEffect(() => {
    if (suite?.id) fetchSuiteEndpoints(suite.id);
  }, [suite?.id, fetchSuiteEndpoints]);

  const handleAddTestCase = (testCase, endpointId) => {
    addToSuite({ testSuiteId: suite.id, endpointId, testCaseId: testCase.id, chainId: -1 });
  };
  const handleAddChain = (chainId) => {
    addToSuite(
      { testSuiteId: suite.id, endpointId: -1, testCaseId: -1, chainId },
      { onSuccess: () => setShowAddChainModal(false) },
    );
  };
  const handleRunSuite = () => runApiSuite({ testSuiteId: suite.id });
  const handleDeleteChain = (chainId) => deleteChain({ testSuiteId: suite.id, chainId });
  const handleDeleteItem = (itemId) =>
    deleteTestCase({ testCaseId: itemId, testSuiteId: suite.id });

  const chainItems = endpoints.filter((item) => item.chainId && item.chainId !== -1);
  const testCaseItems = endpoints.filter(
    (item) => item.testCaseId && item.testCaseId !== -1 && item.testCaseDetails,
  );

  return (
    <div className="font-apple bg-gray-50 h-full max-h-[100vh] overflow-hidden flex flex-col">
      <div className="bg-white border-b border-gray-200 px-5 py-3.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            title="Back to suites"
          >
            <CornerUpLeftIcon size={18} />
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-medium">SUITE</span>
              <ChevronRight size={12} className="text-gray-300" />
            </div>
            <h1 className="text-base font-semibold text-gray-900 leading-tight">
              {suite.testSuiteName}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ActionButton
            onClick={() => onViewResults(suite)}
            icon={ScrollTextIcon}
            label="View Results"
            variant="blue"
          />
          <ActionButton
            onClick={() => setShowAddTestCaseModal(true)}
            icon={PackagePlusIcon}
            label="Add Test Cases"
            variant="blue"
          />
          <ActionButton
            onClick={() => setShowAddChainModal(true)}
            icon={WorkflowIcon}
            label="Add Chains"
            variant="purple"
          />
          <ActionButton
            onClick={handleRunSuite}
            icon={SquarePlayIcon}
            label={runningSuite ? "Running..." : "Run Suite"}
            variant="green"
            disabled={runningSuite}
            loading={runningSuite}
          />
          <ActionButton
            onClick={() => onPerformanceTest(suite)}
            icon={LoaderPinwheelIcon}
            label="Performance Test"
            variant="indigo"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Suite Contents</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Test cases and chains configured in this suite
              </p>
            </div>
            {endpoints.length > 0 && (
              <div className="flex gap-3">
                <span className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-900">{chainItems.length}</span> chain
                  {chainItems.length !== 1 ? "s" : ""}
                </span>
                <span className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-900">{testCaseItems.length}</span> test
                  case{testCaseItems.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>

          {loadingSuiteEndpoints ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-400">Loading suite contents...</span>
              </div>
            </div>
          ) : endpoints.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-2xl flex items-center justify-center mb-4">
                <TestTube size={24} className="text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">No test cases yet</h3>
              <p className="text-xs text-gray-400 mb-5 max-w-xs">
                Add test cases from your collections, or chain multiple requests together to test
                complex API flows.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddTestCaseModal(true)}
                  className="flex items-center gap-2 px-3.5 py-2 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded-lg"
                >
                  <PackagePlusIcon size={13} /> Add Test Cases
                </button>
                <button
                  onClick={() => setShowAddChainModal(true)}
                  className="flex items-center gap-2 px-3.5 py-2 text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors cursor-pointer rounded-lg"
                >
                  <WorkflowIcon size={13} /> Add Chains
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {chainItems.length > 0 && (
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <WorkflowIcon size={14} className="text-purple-500" />
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Chains ({chainItems.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {chainItems.map((item) => (
                      <ChainAccordion
                        key={item.id}
                        item={item}
                        onDelete={() => handleDeleteChain(item.chainId)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {testCaseItems.length > 0 && (
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheckIcon size={14} className="text-blue-500" />
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Test Cases ({testCaseItems.length})
                    </h4>
                  </div>
                  <div className="space-y-1.5">
                    {testCaseItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between px-4 py-3 border border-gray-100 rounded-lg bg-gray-50 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <MethodBadge method={item.testCaseDetails?.httpMethod} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {item.testCaseDetails?.testCase || "Unnamed Test Case"}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <code className="text-xs text-gray-500 font-mono truncate max-w-xs">
                                {item.testCaseDetails?.endpoint || "N/A"}
                              </code>
                              {item.testCaseDetails?.testType && (
                                <>
                                  <span className="text-gray-300">·</span>
                                  <span className="text-xs text-gray-400">
                                    {item.testCaseDetails.testType}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteItem(item.testCaseId)}
                          className="ml-3 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove from suite"
                        >
                          <Trash2Icon size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AddTestCaseModal
        open={showAddTestCaseModal}
        onClose={() => setShowAddTestCaseModal(false)}
        suiteId={suite.id}
        onAddTestCase={handleAddTestCase}
        addingToSuite={addingToSuite}
      />
      <AddChainModal
        open={showAddChainModal}
        onClose={() => setShowAddChainModal(false)}
        suiteId={suite.id}
        onAddChain={handleAddChain}
        addingToSuite={addingToSuite}
      />
    </div>
  );
};

export default SuiteDetails;
